// src/services/propertyService.js
import { buildApiUrl } from '../config/environment';
import { makeAuthenticatedRequest } from './authService';

// API-based function to fetch properties by agency ID
export const getPropertiesByAgency = async (agencyId) => {
  const token = sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  const url = buildApiUrl(`/agencies/${agencyId}/properties`);
  try {
    // Try with direct fetch to avoid CORS preflight
    console.log('[getPropertiesByAgency] Making direct fetch to avoid CORS preflight');
    const fetchResponse = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
        // No Content-Type to avoid preflight
      }
    });
    if (!fetchResponse.ok) {
      if (fetchResponse.status === 401) {
        throw new Error('Authentication failed - token may be expired');
      }
      throw new Error(`HTTP error! status: ${fetchResponse.status}`);
    }
    const data = await fetchResponse.json();
    return data.data || data;
  } catch (error) {
    // Fallback to makeAuthenticatedRequest if direct fetch fails
    try {
      const fallbackResponse = await makeAuthenticatedRequest(url);
      const data = await fallbackResponse.json();
      return data.data || data;
    } catch (fallbackError) {
      console.error('Error fetching properties by agency:', fallbackError);
      throw fallbackError;
    }
  }
};

// Updated getProperties function to use real API
export const getProperties = async (agencyId = null) => {
  if (agencyId) {
    return await getPropertiesByAgency(agencyId);
  }

  // If no agency ID specified, get from session storage
  const sessionAgencyId = sessionStorage.getItem('agencyId');
  if (sessionAgencyId) {
    return await getPropertiesByAgency(sessionAgencyId);
  }

  throw new Error('No agency ID available for fetching properties');
};

// Create property function - calls real API endpoint with images
export const createProperty = async (propertyData) => {
  console.log('[createProperty] Starting property creation...');

  try {
    // Get agency ID from session storage (set during login)
    const agencyId = sessionStorage.getItem('agencyId');
    console.log('[createProperty] agencyId from sessionStorage:', agencyId);
    if (!agencyId) {
      console.error('createProperty: No agency ID in sessionStorage');
      throw new Error('No agency ID available for creating property');
    }

    // Use access_token with fallback to token for backward compatibility
    const token = sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
    console.log('[createProperty] token from sessionStorage:', token ? 'Present' : 'Missing');
    if (!token) {
      console.error('createProperty: No token in sessionStorage');
      throw new Error('Authentication token not found');
    }

    console.log('[createProperty] Session storage contents:', {
      agencyId: sessionStorage.getItem('agencyId'),
      access_token: sessionStorage.getItem('access_token') ? 'Present' : 'Missing',
      token: sessionStorage.getItem('token') ? 'Present' : 'Missing',
      user: sessionStorage.getItem('user') ? 'Present' : 'Missing'
    });

    // Prepare FormData for multipart/form-data
    const formData = new FormData();
    // Append property fields
    Object.entries(propertyData).forEach(([key, value]) => {
      if (key !== 'images' && value !== undefined && value !== null && value !== '') {
        formData.append(key, value);
      }
    });
    // Append images
    if (propertyData.images && propertyData.images.length > 0) {
      propertyData.images.forEach((imgObj, idx) => {
        formData.append('images', imgObj.file);
        if (imgObj.isDefault) {
          formData.append('default_image_index', idx.toString());
        }
      });
    }

    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/properties`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Do not set Content-Type; browser will set it for FormData
      },
      body: formData
    });

    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        // ignore
      }
      console.error('createProperty: API error', response.status, errorData);
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { success: true, property: data };
  } catch (error) {
    console.error('Error creating property:', error);
    return { success: false, error: error.message };
  }
};

// Get single property by ID with full details
export const getPropertyById = async (agencyId, propertyId) => {
  console.log('[getPropertyById] Starting property fetch...', { agencyId, propertyId });

  try {
    const token = sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
    if (!token) {
      console.error('[getPropertyById] No token in sessionStorage');
      throw new Error('Authentication token not found');
    }

    const url = buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}`);
    console.log('[getPropertyById] Making request to:', url);

    // Direct fetch to avoid CORS preflight issues
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('[getPropertyById] Response status:', response.status);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - token may be expired');
      }
      if (response.status === 404) {
        throw new Error('Property not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('[getPropertyById] Property data received:', data);
    return data;
  } catch (error) {
    console.error('Error fetching property by ID:', error);
    throw error;
  }
};

// Get rental listings for a property
export const getRentalListings = async (agencyId, propertyId) => {
  console.log('[getRentalListings] Starting rental listings fetch...', { agencyId, propertyId });

  try {
    const token = sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
    if (!token) {
      console.error('[getRentalListings] No token in sessionStorage');
      throw new Error('Authentication token not found');
    }

    const url = buildApiUrl(`/agencies/${agencyId}/properties/${propertyId}/rental-listings`);
    console.log('[getRentalListings] Making request to:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('[getRentalListings] Response status:', response.status);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - token may be expired');
      }
      if (response.status === 404) {
        throw new Error('Rental listings not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('[getRentalListings] Rental listings data received:', data);
    return data;
  } catch (error) {
    console.error('Error fetching rental listings:', error);
    throw error;
  }
};
